import React, { useRef, useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShoppingBag, Stethoscope, UtensilsCrossed, Wifi, Lock } from "lucide-react";

const JEW = "https://images.unsplash.com/photo-1774110073583-2475ab5ed8b2?crop=entropy&cs=srgb&fm=jpg&w=640&q=70";
const CLINIC = "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&w=640&q=70";
const REST = "https://images.unsplash.com/photo-1496568738682-da9d7fa2f12d?crop=entropy&cs=srgb&fm=jpg&w=640&q=70";

function BrowserCard({ title, img, icon: Icon, tint, className = "", style = {}, badge }) {
  return (
    <div className={`browser-float glass-strong rounded-2xl overflow-hidden shadow-2xl ${className}`} style={style}>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-black/30 border-b border-white/10">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
        <div className="ml-2 flex-1 flex items-center gap-1.5 bg-white/5 rounded-md px-2 py-1 text-[10px] text-slate-400 font-mono">
          <Lock className="w-2.5 h-2.5" /> {title}
        </div>
        <Wifi className="w-3.5 h-3.5 text-slate-500" />
      </div>
      <div className="relative">
        <img src={img} alt={title} loading="lazy" className="w-full h-32 sm:h-36 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-transparent to-transparent" />
        <div className={`absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${tint}`}>
          <Icon className="w-3 h-3" /> {badge}
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="h-2 w-2/3 rounded bg-white/15" />
        <div className="h-2 w-1/2 rounded bg-white/10" />
        <div className="flex gap-1.5 pt-1">
          <div className="h-6 w-16 rounded-md bg-violet-500/70" />
          <div className="h-6 w-16 rounded-md bg-white/10" />
        </div>
      </div>
    </div>
  );
}

export default function Hero3D() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (reduce) return;
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      setTilt({ x: py * -10, y: px * 12 });
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, [reduce]);

  // Static, non-animated fallback for reduced motion
  if (reduce) {
    return (
      <div className="grid grid-cols-2 gap-4" data-testid="hero-3d-fallback">
        <BrowserCard title="aurelia-jewels.com" img={JEW} icon={ShoppingBag} tint="bg-violet-500/90 text-white" badge="Jewellery" />
        <BrowserCard title="clearview-dental.com" img={CLINIC} icon={Stethoscope} tint="bg-cyan-500/90 text-[#06283b]" badge="Clinic" className="mt-8" />
        <BrowserCard title="saffron-smoke.com" img={REST} icon={UtensilsCrossed} tint="bg-emerald-500/90 text-[#063d2e]" badge="Restaurant" className="col-span-2 max-w-sm mx-auto" />
      </div>
    );
  }

  return (
    <div ref={ref} className="scene relative h-[440px] sm:h-[500px]" data-testid="hero-3d-scene">
      <motion.div
        className="absolute inset-0"
        style={{ transformStyle: "preserve-3d" }}
        animate={{ rotateX: tilt.x, rotateY: tilt.y }}
        transition={{ type: "spring", stiffness: 60, damping: 18 }}
      >
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="absolute left-0 top-6 w-56 sm:w-64 anim-float" style={{ transform: "translateZ(60px)" }}>
          <BrowserCard title="aurelia-jewels.com" img={JEW} icon={ShoppingBag} tint="bg-violet-500/90 text-white" badge="Jewellery" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
          className="absolute right-0 top-0 w-56 sm:w-64 anim-float-slow" style={{ transform: "translateZ(110px)" }}>
          <BrowserCard title="clearview-dental.com" img={CLINIC} icon={Stethoscope} tint="bg-cyan-500/90 text-[#06283b]" badge="Clinic" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
          className="absolute left-1/2 -translate-x-1/2 bottom-0 w-56 sm:w-64 anim-float" style={{ transform: "translateZ(30px)" }}>
          <BrowserCard title="saffron-smoke.com" img={REST} icon={UtensilsCrossed} tint="bg-emerald-500/90 text-[#063d2e]" badge="Restaurant" />
        </motion.div>
      </motion.div>
    </div>
  );
}
